import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { tailspin } from 'ldrs';
import { useSocket } from '../../../socket/socketContext.js';
import { motion } from 'framer-motion';

tailspin.register();

const SalesforceObjects = ({ onAddRecordTypes }) => {
  const chatUserObjects = useSelector((state) => state.chatUserObjects.chatUserObjects);
  const [optimisticChatUserObjects, setOptimisticChatUserObjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availableObjects, setAvailableObjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingAdditions, setPendingAdditions] = useState([]);
  const [showAddButton, setShowAddButton] = useState(true);
  const socket = useSocket();

  // Listen for available object names from the server.
  useEffect(() => {
    if (!socket) return;
    const handleGetObjectNamesResults = (results) => {
      // Expecting results to be an array of objects with { name, label }
      setAvailableObjects(results);
      setLoading(false);
    };

    socket.on('getObjectNamesResults', handleGetObjectNamesResults);
    return () => {
      socket.off('getObjectNamesResults', handleGetObjectNamesResults);
    };
  }, [socket]);

  // Listen for the server response when an object is added.
  useEffect(() => {
    if (!socket) return;
    const handleSalesforceObjectAdded = (data) => {
      // Data is expected to have the format: { objectName, success }
      const { objectName, success } = data;
      if (success) {
        // Remove from pending.
        setPendingAdditions((prev) => prev.filter((item) => item.name !== objectName));
        // Optimistically mark as added by updating local state.
        setOptimisticChatUserObjects((prev) => {
          if (prev.some((item) => item.name === objectName)) return prev;
          const addedObj = availableObjects.find((o) => o.name === objectName);
          return addedObj ? [...prev, addedObj] : prev;
        });
      } else {
        // For failures, remove from pending and log an error.
        setPendingAdditions((prev) => prev.filter((item) => item.name !== objectName));
        console.error(`Failed to add ${objectName}`);
      }
    };
    socket.on('salesforceObjectAdded', handleSalesforceObjectAdded);
    return () => {
      socket.off('salesforceObjectAdded', handleSalesforceObjectAdded);
    };
  }, [socket, availableObjects]);

  // Compute a combined list of already added objects.
  const displayedAddedObjects = [
    ...chatUserObjects,
    ...optimisticChatUserObjects.filter(
      (o) => !chatUserObjects.some((x) => x.name === o.name)
    ),
  ];

  // Build a frequency map for labels to detect duplicates.
  const labelCounts = availableObjects.reduce((acc, obj) => {
    const label = obj.label;
    acc[label] = (acc[label] || 0) + 1;
    return acc;
  }, {});

  // Filter available objects based on search term and sort by relevance.
  const sortedFilteredAvailableObjects = availableObjects
    .filter((obj) =>
      obj.label.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const search = searchTerm.toLowerCase();
      const aLabel = a.label.toLowerCase();
      const bLabel = b.label.toLowerCase();
      const aStarts = aLabel.startsWith(search) ? 0 : 1;
      const bStarts = bLabel.startsWith(search) ? 0 : 1;
      if (aStarts !== bStarts) {
        return aStarts - bStarts;
      }
      return aLabel.localeCompare(bLabel);
    });

  // Handle clicking the "Add Record Types" button.
  const handleAddRecordType = () => {
    if (socket) {
      setLoading(true);
      setShowAddButton(false); // Hide the button after click
      // Trigger parent's callback to mark that refresh is needed.
      if (typeof onAddRecordTypes === 'function') {
        onAddRecordTypes();
      }
      socket.emit('getObjectNames');
    }
  };

  // When a user clicks an available record type, add it to pending state and emit the event.
  const handleSelectRecordType = (obj) => {
    const isAlreadyAdded =
      chatUserObjects.some((o) => o.name === obj.name) ||
      optimisticChatUserObjects.some((o) => o.name === obj.name);
    if (isAlreadyAdded || pendingAdditions.some((item) => item.name === obj.name)) {
      return;
    }
    setPendingAdditions((prev) => [...prev, { ...obj, status: 'adding' }]);
    socket.emit('addSalesforceObject', { objectName: obj.name });
  };

  return (
    <div>
      <h4 className="font-semibold mb-2">Salesforce Record Types</h4>
      <p className="mt-1 text-sm text-gray-800">
        Tarn is currently set up to work with these objects:
      </p>
      {/* Top Section: Display pending additions (in gray, pulsing) and already-added objects */}
      <div className="mt-4 p-4 border border-gray-300 bg-[#f9f9f9] rounded-lg flex flex-wrap gap-3 h-28 overflow-y-auto">
        {/* Pending Additions */}
        {pendingAdditions.map((obj, index) => (
          <motion.div
            key={obj.name || index}
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1, repeat: Infinity }}
            className="h-[36px] flex items-center border border-gray-200 bg-gray-100 rounded-lg px-3"
          >
            <p className="text-xs text-gray-600 font-semibold whitespace-nowrap">
              Adding {obj.label}
            </p>
          </motion.div>
        ))}
        {/* Already Added Objects */}
        {displayedAddedObjects.map((obj, index) => (
          <div
            key={obj.name || index}
            className="h-[36px] flex items-center border border-gray-200 bg-white rounded-lg px-3"
          >
            <p className="text-xs text-indigo-600 font-semibold whitespace-nowrap">
              {obj.label}
            </p>
          </div>
        ))}
      </div>

      {/* Available Record Types */}
      {availableObjects.length > 0 && (
        <>
          <div className="mt-5 mr-6 flex items-center">
            <p className="text-sm text-gray-800">
              Choose additional record types:
            </p>
            <input
              type="text"
              placeholder="search for objects"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="ml-3 border border-gray-300 rounded px-2 py-1 text-sm"
            />
          </div>
          <div className="mt-4 p-4 border border-gray-300 bg-[#f9f9f9] rounded-lg flex flex-wrap gap-3 h-36 overflow-y-auto">
            {sortedFilteredAvailableObjects.map((obj, index) => {
              const isPending = pendingAdditions.some((item) => item.name === obj.name);
              const isAdded =
                chatUserObjects.some((o) => o.name === obj.name) ||
                optimisticChatUserObjects.some((o) => o.name === obj.name);
              const displayLabel =
                labelCounts[obj.label] > 1 ? `${obj.label} - '${obj.name}'` : obj.label;
              return (
                <div
                  key={obj.name || index}
                  onClick={!isAdded && !isPending ? () => handleSelectRecordType(obj) : undefined}
                  className={`p-3 border ${
                    isPending
                      ? 'border-gray-300 opacity-50'
                      : isAdded
                      ? 'border-indigo-600'
                      : 'border-gray-200'
                  } bg-white rounded-lg ${isAdded || isPending ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <p
                    className={`text-xs font-semibold whitespace-nowrap ${
                      isAdded ? 'text-indigo-600' : isPending ? 'text-gray-400' : 'text-gray-700'
                    }`}
                  >
                    {displayLabel}
                  </p>
                </div>
              );
            })}
          </div>
        </>
      )}

      <div className="flex justify-end mt-4">
        {loading && (
          <l-tailspin size="40" stroke="5" speed="0.9" color="#6366F1"></l-tailspin>
        )}
        {showAddButton && (
          <button
            onClick={handleAddRecordType}
            className="px-3 py-2 bg-indigo-600 text-white text-xsm rounded hover:bg-indigo-700 focus:outline-none"
          >
            Add Record Types
          </button>
        )}
      </div>
    </div>
  );
};

export default SalesforceObjects;